import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  SearchResult as SearchResultType,
  recentSearches,
  projectSearchResults,
  pitchDeckSearchResults
} from 'data/search';
import Scrollbar from 'components/base/Scrollbar';
import useSearchHook from 'hooks/useSearchHook';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { faClockRotateLeft, faLink } from '@fortawesome/free-solid-svg-icons';

const ResultSectionHeader = ({ title }: { title: string }) => {
  return (
    <h6 className="text-1000 fs-9 border-y border-200 py-2 lh-sm mb-0 px-3">
      {title}
    </h6>
  );
};

const searchFields: ColumnDef<SearchResultType>[] = [
  {
    accessorKey: 'label'
  }
];

const SearchResult = ({
  searchValue = '',
  setSearchInputValue
}: {
  searchValue?: string;
  setSearchInputValue?: (value: string) => void;
}) => {
  const recentSearchesBack = useSearchHook(
    recentSearches,
    searchFields,
    searchValue
  );

  const projectSearchResultsBack = useSearchHook(
    projectSearchResults,
    searchFields,
    searchValue
  );

  const pitchDeckSearchResultsBack = useSearchHook(
    pitchDeckSearchResults,
    searchFields,
    searchValue
  );

  const recentlySearchedItems = useMemo(
    () =>
      recentSearchesBack.filter(item => item.category === 'recently_searched'),
    [recentSearchesBack]
  );

  const projects = useMemo(
    () => projectSearchResultsBack.filter(item => item.category === 'projects'),
    [projectSearchResultsBack]
  );

  const pitchDecks = useMemo(
    () =>
      pitchDeckSearchResultsBack.filter(
        item => item.category === 'pitch_decks'
      ),
    [pitchDeckSearchResultsBack]
  );

  const handleRecentClick = (label: string) => () => {
    if (setSearchInputValue) {
      setSearchInputValue(label);
    }
  };

  return (
    <Scrollbar autoHeight autoHeightMax={'30rem'}>
      <h6 className="text-1000 fs-10 py-2 mb-0 px-3">
        {projectSearchResults.length} <span className="text-500">Projects</span>{' '}
        {pitchDeckSearchResults.length}{' '}
        <span className="text-500">PitchDecks</span>{' '}
      </h6>
      {recentlySearchedItems.length > 0 && (
        <>
          <ResultSectionHeader title="Recently Searched" />
          <div className="py-2">
            {recentlySearchedItems.map(item => (
              <Dropdown.ItemText
                as={Link}
                to={'google.com'}
                key={item.label}
                onClick={handleRecentClick(item.label)}
              >
                <div
                  className="d-flex align-items-center fw-normal gap-1 text-1000"
                  key={item.label}
                >
                  <FontAwesomeIcon
                    icon={faClockRotateLeft}
                    transform="shrink-2"
                  />
                  {item.label}
                </div>
              </Dropdown.ItemText>
            ))}
          </div>
        </>
      )}

      {projects.length > 0 && (
        <>
          <ResultSectionHeader title="Projects" />
          <div className="py-2">
            {projects.map(item => (
              <Dropdown.Item
                as={Link}
                to={item.url}
                key={item.label}
                className="py-2 d-flex gap-2 align-items-center"
              >
                <div className="file-thumbnail">
                  <img
                    className="fit-cover rounded-3"
                    src={item.image}
                    height={28}
                    width={28}
                    alt={item.label}
                  />
                </div>
                <div className="flex-1">
                  <h6 className="mb-0 text-1000">{item.label}</h6>
                  <p className="fs-10 mb-0 d-flex text-700">
                    <span className="fw-medium text-600">{item.details}</span>
                  </p>
                </div>
              </Dropdown.Item>
            ))}
          </div>
        </>
      )}
      {pitchDecks.length > 0 && (
        <>
          <ResultSectionHeader title="PitchDecks" />
          <div className="py-2">
            {pitchDecks.map(item => (
              <Dropdown.Item as={Link} to={item.url} key={item.label}>
                <div className="d-flex align-items-center fw-normal gap-1 text-1000">
                  <FontAwesomeIcon
                    icon={faLink}
                    transform="shrink-2"
                    className="text-900"
                  />
                  {item.label}
                </div>
              </Dropdown.Item>
            ))}
          </div>
        </>
      )}
    </Scrollbar>
  );
};

export default SearchResult;
